/**
 * AsyncStorage Shim for Web
 * 
 * This provides a compatible interface for AsyncStorage on the web
 * using localStorage as the underlying storage mechanism.
 */
class AsyncStorage {
  static async getItem(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  static async setItem(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      throw error;
    }
  }

  static async removeItem(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      throw error;
    }
  }

  static async clear() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      throw error;
    }
  }

  static async multiGet(keys) {
    try {
      return keys.map(key => [key, localStorage.getItem(key)]);
    } catch (error) {
      console.error('Error reading multiple keys from localStorage:', error);
      throw error;
    }
  }

  static async multiSet(keyValuePairs) {
    try {
      keyValuePairs.forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      return true;
    } catch (error) {
      console.error('Error writing multiple keys to localStorage:', error);
      throw error;
    }
  }

  static async multiRemove(keys) {
    try {
      keys.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.error('Error removing multiple keys from localStorage:', error);
      throw error;
    }
  }
}

export default AsyncStorage;

// Hooks for AsyncStorage compatibility
export const useAsyncStorage = (key) => {
  return {
    getItem: async () => await AsyncStorage.getItem(key),
    setItem: async (value) => await AsyncStorage.setItem(key, value),
    removeItem: async () => await AsyncStorage.removeItem(key),
  };
}; 